import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { mapArrayBy } from "@sdflc/utils";

import { loadLanguage } from "@/i18n";

import siteMap from "@/config/siteMap";
import { SiteMapItem } from "@/config/siteMap";
import { USER_ROLE_RIGHTS_ENTITIES } from "@/config/userRoles";

import { getFeatureValue } from "@/utils/featureValues";
import { evaluateEntitlement } from "@/utils/planEntitlement";
import { parseFlexibleDate } from "@/utils/misc";
import { getCookie, setCookie } from "@/utils/cookies";

import { User } from "@/utils/api/UserService";
import { AppPlanFeatureDetails } from "@/utils/api/AppPlanService";
import { AuthService, type SignUpParams } from "@/utils/api/AuthService";
import { CrewSessionService } from "@/utils/api/CrewSessionService";
import { refreshSessionOnce, sessionTokenExpiresAt } from "@em/api";
import { USER_ROLES, canonicalRoleId } from "@/config/userRoles";

export interface AppContextValue {
  // Site Map
  siteMap: SiteMapItem[];

  // User Management
  user: User | null;
  isAuthenticated: boolean;
  userRoles: string[];
  userRolesNames: string[];
  appPlansFeatures: Record<string, AppPlanFeatureDetails>;
  trialEndsAt: Date | null | undefined;
  subscriptionStatus: string;
  isInitializing: boolean;

  // User Actions
  setUser: (user: User | null) => void;
  signOut: () => Promise<void>;
  signInWithCredentials: (
    username: string,
    password: string
  ) => Promise<OpResult>;
  signUpWithCredentials: (params: SignUpParams) => Promise<OpResult>;

  // App Plan Features
  hasAppPlanFeature: (featureCode: string) => boolean;
  getAppPlanFeature: (
    featureCode: string
  ) => AppPlanFeatureDetails | null | undefined;
  chechAppPlanFeature: (featureCode: string, featureValue: any) => OpResult;
  hasRightTo: (chkEntity: string, chkOperation: string) => boolean;

  // Navigation Helpers
  getVisibleRoutes: () => SiteMapItem[];
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

interface AppProviderProps {
  children: React.ReactNode;
}

const authPages = [
  "/account/signin",
  "/account/signup",
  "/account/forgot-password",
  "/account/reset-password",
  "/account/accept-invitation",
  // The crew sign-in page is public and must never bounce a visitor to account
  // sign-in — a non-account crew member has no account (docs/CREW-ACCESS.md).
  "/crew-access",
];

// A FE-only synthetic user for a NON-account crew session, so the app treats the
// crew member as "signed in" (no account-signin redirect) and the event routes
// render for them. Carries no account; every action is authorized server-side by
// the crew token, and CrewAccessContext filters the nav to real capabilities.
/**
 * ROADMAP #239 — a session found good at boot whose token lifetime this browser does not know (opened before #239 shipped,
 * or storage cleared) is renewed once, in the background, to learn it: until then nothing can renew AHEAD of its expiry,
 * and the actions that read a token only if there is one would answer an expired one as anonymous (empty data).
 */
const learnSessionLifetime = (): void => {
  if (sessionTokenExpiresAt() == null) {
    void refreshSessionOnce(Date.now());
  }
};

const buildCrewUser = (): User =>
  ({
    id: "crew-session",
    username: "Crew",
    userRole: { id: USER_ROLES.CREW, roleName: "Crew" },
  } as unknown as User);

// Get cookie duration from environment variable, default to 30 days
const FSQS_COOKIE_DAYS = Number(import.meta.env.VITE_FSQS_COOKIE_DAYS) || 30;
const FSQS_COOKIE_NAME = import.meta.env.VITE_fSQS_COOKIE_NAME || "vbqs";
const FSQSRF_COOKIE_NAME = `${FSQS_COOKIE_NAME}rf`;

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [user, setRawUserState] = useState<User | null>(null);
  // The signed-in user's role is held as the role `USER_ROLES` names, whichever brand's row of it they have
  // (`canonicalRoleId`) — every access group, right and "is owner" in the app compares against those.
  const setUserState = useCallback((next: User | null) => {
    setRawUserState(
      next?.userRole?.id && canonicalRoleId(next.userRole.id) !== next.userRole.id
        ? { ...next, userRole: { ...next.userRole, id: canonicalRoleId(next.userRole.id) } }
        : next,
    );
  }, []);
  const [isInitializing, setIsInitializing] = useState(true);

  // Computed values
  const isAuthenticated = useMemo(() => Boolean(user?.id), [user]);

  const userRoles = useMemo(() => {
    if (isAuthenticated && user?.userRole) {
      return [user?.userRole.id || ""];
    }
    return [""]; // Default role for non-authenticated users
  }, [isAuthenticated, user]);

  const userRolesNames = useMemo(() => {
    if (isAuthenticated && user?.userRole) {
      return [user.userRole?.roleName || ""];
    }
    return [""]; // Default role for non-authenticated users
  }, [isAuthenticated, user]);

  // Parse trial end date
  const trialEndsAt = user?.account?.trialEndsAt
    ? parseFlexibleDate(user?.account?.trialEndsAt)?.toDate()
    : null;

  const isOnTrial = trialEndsAt != null && trialEndsAt > new Date();

  // Determine subscription status
  const subscriptionStatus = useMemo(() => {
    if (isOnTrial) return "trial";
    if (user?.account?.psSubscriptionStatus === "active") return "active";
    if (user?.account?.psSubscriptionStatus === "cancelled") return "cancelled";

    return !trialEndsAt ? "active" : "expired";
  }, [user, isOnTrial, trialEndsAt]);

  const appPlansFeatures = useMemo(() => {
    if (Array.isArray(user?.account?.appPlan?.planFeaturesDetails)) {
      return mapArrayBy(
        user?.account?.appPlan?.planFeaturesDetails,
        "featureCode"
      );
    }

    return {};
  }, [user]);

  // ============================================
  // Initialize UI language from user profile
  // ============================================

  useEffect(() => {
    if (user?.uiLanguage) {
      loadLanguage(user.uiLanguage);
    }
  }, [user?.uiLanguage]);

  // User management functions
  const setUser = useCallback((newUser: User | null) => {
    setUserState(newUser);

    // Store/remove user data in localStorage for persistence
    if (newUser) {
      AuthService.storeUserData(newUser);
    } else {
      AuthService.clearStoredUserData();
    }
  }, [setUserState]);

  const signOut = useCallback(async () => {
    // A crew session has no account to sign out of — just drop the crew token.
    if (CrewSessionService.hasSession()) {
      CrewSessionService.clear();
      setUserState(null);
      return;
    }

    try {
      await AuthService.signOut();
    } catch (error) {
      console.error("Error during sign out:", error);
    }
    // Always clear local user state regardless of API result
    setUserState(null);
  }, [setUserState]);

  // Convert API user data to our User interface
  const convertApiUserToUser = useCallback((apiUser: any): User => {
    return {
      ...apiUser,
      //roles: ["admin"], // Default to admin role, this should come from userAccounts
      /* id: apiUser.id,
      username: apiUser.username,
      email: apiUser.email,
      name: apiUser.username, // Use username as display name for now
      
      isAuthenticated: true,
      ...(apiUser.userAccounts && {
        userAccounts: apiUser.userAccounts,
      }),
      ...(apiUser.uiLanguage && {
        uiLanguage: apiUser.uiLanguage,
      }),
      ...(apiUser.status && {
        status: apiUser.status,
      }), */
    };
  }, []);

  // Initialize user session on app start using 'me' query
  const initializeUserSession = useCallback(async () => {
    setIsInitializing(true);

    try {
      // Check if we have stored user data
      const storedUserData = AuthService.getStoredUserData();

      // NON-account crew session (docs/CREW-ACCESS.md, scenario 3): a crew token
      // but no account login. Treat the crew member as signed in with a synthetic
      // user and SKIP the `me()` account check entirely (it would 401 and eject
      // them). An account login always takes precedence over a crew token.
      if (!storedUserData && CrewSessionService.hasSession()) {
        setUserState(buildCrewUser());
        setIsInitializing(false);
        return;
      }

      if (storedUserData) {
        // Use 'me' query to validate current session and get fresh user data
        const meResult = await AuthService.me();

        if (meResult.didSucceedAndHasData()) {
          // Session is valid, update user data with fresh info
          const freshUserData = meResult.getDataFirst();
          const user = convertApiUserToUser(freshUserData);
          setUserState(user);
          learnSessionLifetime();
        } else {
          // Session expired or invalid, clear stored data
          AuthService.clearStoredUserData();
          setUserState(null);

          if (
            meResult.code === OP_RESULT_CODES.UNAUTHORIZED &&
            !authPages.includes(window.location.pathname)
          ) {
            // ⚠ Encoded: a raw "&" in the page's own query cut the path short, and "+" came back as a space.
            window.location.href = `/account/signin?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;
          }
        }
      } else {
        // No stored user data, but check if there might be valid cookies
        // This handles cases where localStorage was cleared but cookies are still valid
        const meResult = await AuthService.me();

        if (meResult.didSucceedAndHasData()) {
          // Valid session found, restore user data
          const userData = meResult.getDataFirst();
          const user = convertApiUserToUser(userData);
          setUserState(user);
          learnSessionLifetime();
        } else {
          // No valid session
          setUserState(null);

          if (
            meResult.code === OP_RESULT_CODES.UNAUTHORIZED &&
            !authPages.includes(window.location.pathname)
          ) {
            // ⚠ Encoded: a raw "&" in the page's own query cut the path short, and "+" came back as a space.
            window.location.href = `/account/signin?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;
          }
        }
      }
    } catch (error) {
      console.error("Error initializing user session:", error);
      // Clear any invalid stored data
      AuthService.clearStoredUserData();
      setUserState(null);
    } finally {
      setIsInitializing(false);
    }
  }, [convertApiUserToUser, setUserState]);

  // Sign in function for forms to use
  const signInWithCredentials = useCallback(
    async (username: string, password: string): Promise<OpResult> => {
      const result = await AuthService.signIn({ username, password });

      if (result.didSucceedAndHasData()) {
        const userData = result.getDataFirst();
        const user = convertApiUserToUser(userData);

        setUser(user);
      }

      return result;
    },
    [convertApiUserToUser, setUser]
  );

  // Sign up function for forms to use
  const signUpWithCredentials = useCallback(
    async (params: SignUpParams): Promise<OpResult> => {
      if (!params.urlQuery) {
        params.urlQuery = getCookie(FSQS_COOKIE_NAME) ?? "";
        params.httpReferer = getCookie(FSQSRF_COOKIE_NAME) ?? "";

        if (!params.urlQuery) {
          const queryString = window.location.search;

          // Only set cookie if there's a query string
          if (queryString) {
            setCookie(FSQS_COOKIE_NAME, queryString, FSQS_COOKIE_DAYS);
            params.urlQuery = queryString;
          }

          const referer = document.referrer;

          if (referer) {
            setCookie(FSQSRF_COOKIE_NAME, referer, FSQS_COOKIE_DAYS);
            params.httpReferer = referer;
          }
        }
      }

      const result = await AuthService.signUp(params);

      if (result.didSucceedAndHasData()) {
        const userData = result.getDataFirst();
        const user = convertApiUserToUser(userData);
        setUser(user);
      }

      return result;
    },
    [convertApiUserToUser, setUser]
  );

  // Navigation helper functions
  const getVisibleRoutes = useCallback(() => {
    // Helper function to check if item is accessible by user
    const isAccessible = (item: SiteMapItem): boolean => {
      // Must be visible
      if (!item.visible) return false;

      // Check if user has required roles
      if (item.roles && item.roles.length > 0) {
        const hasRequiredRole = item.roles.some((role) =>
          userRoles.includes(role)
        );
        if (!hasRequiredRole) return false;
      }

      return true;
    };

    // Recursive function to filter items and their children
    const filterItemWithChildren = (item: SiteMapItem): SiteMapItem | null => {
      // Check if current item is accessible
      if (!isAccessible(item)) return null;

      // If item has children, filter them recursively
      if (item.items && item.items.length > 0) {
        const visibleChildren = item.items
          .map((child) => filterItemWithChildren(child))
          .filter((child): child is SiteMapItem => child !== null);

        // Only include parent if it has visible children OR has a URL itself
        if (visibleChildren.length > 0 || item.url) {
          return {
            ...item,
            items: visibleChildren,
          };
        }

        // Parent with no visible children and no URL should be filtered out
        return null;
      }

      // Leaf item with URL - include it
      if (item.url) {
        return item;
      }

      // Item without children and without URL - shouldn't happen but filter out
      return null;
    };

    // Filter all top-level items
    return siteMap
      .map((item) => filterItemWithChildren(item))
      .filter((item): item is SiteMapItem => item !== null);
  }, [userRoles]);

  const hasRole = useCallback(
    (role: string) => {
      return userRoles.includes(role);
    },
    [userRoles]
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => {
      return roles.some((role) => userRoles.includes(role));
    },
    [userRoles]
  );

  /**
   * ⚠ ACCOUNT-ONLY, and deliberately so — `useFeatureGate().hasFeature(code, { eventId })` is
   * the function call sites must use, because it is the only one that will learn about
   * event-scoped grants when they land (MONETIZATION.md §2). This stays for the few places
   * that legitimately have no event in hand.
   *
   * ⚠ It used to return `!!appPlansFeatures[code]` — the presence of the ROW, not its value.
   * Every account carries a row for every provisioned feature, so a plan with
   * `guest_list = false` answered TRUE and the gate said "included" about the one thing the
   * server was refusing. The verdict now comes from `evaluateEntitlement`, which mirrors the
   * server's `planFeatureGate` rule for rule.
   */
  const hasAppPlanFeature = useCallback(
    (featureCode: string): boolean => {
      return evaluateEntitlement(appPlansFeatures, featureCode).allowed;
    },
    [appPlansFeatures]
  );

  const getAppPlanFeature = useCallback(
    (featureCode: string): AppPlanFeatureDetails | null | undefined => {
      return appPlansFeatures?.[featureCode];
    },
    [appPlansFeatures]
  );

  const chechAppPlanFeature = useCallback(
    (featureCode: string, featureValue: any): OpResult => {
      const appPlanFeaute = appPlansFeatures?.[featureCode];
      if (appPlanFeaute) {
        const value = getFeatureValue(
          appPlanFeaute.featureValue,
          appPlanFeaute.featureValueType
        );

        // Check boolean features
        if (
          appPlanFeaute.featureValueType === "boolean" &&
          value !== featureValue
        ) {
          return OpResult.fail(
            OP_RESULT_CODES.NOT_IMPLEMETED,
            [],
            `This feature requires another plan`
          );
        }

        // Check numeric limits (e.g., total screens, display configs)
        if (typeof featureValue === "number" && typeof value === "number") {
          if (featureValue >= value) {
            return OpResult.fail(
              OP_RESULT_CODES.LIMIT_REACHED,
              [],
              `Plan limit exceeded (${value} allowed)`
            );
          }
        }

        return OpResult.ok();
      }

      return OpResult.fail(
        OP_RESULT_CODES.NOT_IMPLEMETED,
        [],
        `This feature not found in the user plan`
      );
    },
    [appPlansFeatures]
  );

  const hasRightTo = useCallback(
    (chkEntity: string, chkOperation: string): boolean => {
      const rights = user?.userRole?.id
        ? USER_ROLE_RIGHTS_ENTITIES[user?.userRole?.id]
        : null;

      if (!rights) {
        console.log(
          `There are no rights configuration for current user's ${user?.username ?? "--"
          } role '${user?.userRole?.id ?? "--"
          }' for entity ${chkEntity} and operation ${chkOperation}`
        );
        return false;
      }

      const entity = rights[chkEntity];

      if (!entity) {
        console.log(
          `Entity ${chkEntity} was not found for current user's ${user?.username ?? "--"
          } role '${user?.userRole?.id ?? "--"}' and operation ${chkOperation}`
        );
        return false;
      }

      const hasRight = entity.includes(chkOperation);

      if (!hasRight) {
        console.log(
          `Current user ${user?.username ?? "--"} with role '${user?.userRole?.id ?? "--"
          }' has no rights with operation ${chkOperation} for entity ${chkEntity}`
        );
      }

      return hasRight;
    },
    [user]
  );

  // Initialize vbqs cookie tracking
  useEffect(() => {
    const vbqsCookie = getCookie(FSQS_COOKIE_NAME);

    // If cookie doesn't exist or is empty
    if (!vbqsCookie) {
      const queryString = window.location.search;

      // Only set cookie if there's a query string
      if (queryString) {
        setCookie(FSQS_COOKIE_NAME, queryString, FSQS_COOKIE_DAYS);
      }

      const referer = document.referrer;

      if (referer) {
        setCookie(FSQSRF_COOKIE_NAME, referer, FSQS_COOKIE_DAYS);
      }
    }
  }, []);

  // Initialize user session on app start
  useEffect(() => {
    initializeUserSession();
  }, [initializeUserSession]);

  const contextValue: AppContextValue = {
    // Site Map
    siteMap,

    // User Management
    user,
    isAuthenticated,
    userRoles,
    userRolesNames,
    appPlansFeatures,
    trialEndsAt,
    subscriptionStatus,
    isInitializing,

    // User Actions
    setUser,
    signOut,
    signInWithCredentials,
    signUpWithCredentials,

    // App Plan Features
    hasAppPlanFeature,
    getAppPlanFeature,
    chechAppPlanFeature,
    hasRightTo,

    // Navigation Helpers
    getVisibleRoutes,
    hasRole,
    hasAnyRole,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Custom hook for using the app context
export const useApp = (): AppContextValue => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
